import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../config";

const baseUrl = BASE_URL + "version";

/** Normalize API payload to a results array (several backend shapes). */
export function extractVersionHistoryResults(payload) {
  if (!payload) return [];
  return payload.data?.results ?? payload.results ?? [];
}

export function versionHistoryRowDedupeKey(item) {
  if (!item) return "";
  return (
    item.audit_trail_id ||
    item.audit_trail_id_ref ||
    `${item.version_id ?? ""}|${item.audit_date ?? ""}|${item.action ?? ""}`
  );
}

// 📌 Create Version History (POST)
export const createVersionHistory = createAsyncThunk(
  "versionHistory/create",
  async (versionData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${baseUrl}/create`, versionData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error creating version");
    }
  }
);

// 📌 Update Version History (PUT)
export const updateVersionHistory = createAsyncThunk(
  "versionHistory/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${baseUrl}/update/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating version");
    }
  }
);

// 📌 Get Version History by ID (GET)
export const getVersionHistoryById = createAsyncThunk(
  "versionHistory/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/get-by-id/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching version");
    }
  }
);

// 📌 Get All Version Histories (GET)
// Supports: versionId (single or comma‑separated UUIDs), procedureRevisionId + partRef for one call per procedure revision, limit/offset.
export const getAllVersionHistories = createAsyncThunk(
  "versionHistory/getAll",
  async (opts = {}, { rejectWithValue }) => {
    const {
      limit: limitIn,
      offset: offsetIn,
      versionId,
      procedureRevisionId,
      partRef,
    } = opts;
    const hasScopedFilter =
      (versionId != null && String(versionId).trim() !== "") ||
      (procedureRevisionId != null && String(procedureRevisionId).trim() !== "") ||
      (partRef != null && String(partRef).trim() !== "");
    const defaultLimit = hasScopedFilter ? 200000 : 100;
    const limit =
      limitIn !== undefined && limitIn !== null ? limitIn : defaultLimit;
    const offset = offsetIn !== undefined && offsetIn !== null ? offsetIn : 0;
    /** Keep bare get-all (no filters) with no query params, matching legacy callers. */
    const shouldSendPagination =
      hasScopedFilter || limitIn !== undefined || offsetIn !== undefined;
    try {
      const params = {};
      if (shouldSendPagination) {
        params.limit = limit;
        params.offset = offset;
      }
      if (versionId != null && String(versionId).trim() !== "") {
        params.version_id = String(versionId).trim();
      }
      if (procedureRevisionId != null && String(procedureRevisionId).trim() !== "") {
        params.procedure_revision_id = String(procedureRevisionId).trim();
      }
      if (partRef != null && String(partRef).trim() !== "") {
        params.partRef = String(partRef).trim();
      }
      const response = await axios.get(`${baseUrl}/get-all`, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching versions");
    }
  }
);

// 📌 Delete Version History (DELETE)
export const deleteVersionHistory = createAsyncThunk(
  "versionHistory/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${baseUrl}/delete/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error deleting version");
    }
  }
);

// 🛠 Version History Slice
const versionHistorySlice = createSlice({
  name: "versionHistory",
  initialState: {
    versionHistories: [],
    versionHistory: null,
    loading: false,
    error: null,
    pagination: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createVersionHistory.pending, (state) => {
        state.loading = true;
      })
      .addCase(createVersionHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.versionHistories.push(action.payload);
      })
      .addCase(createVersionHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      })
      .addCase(updateVersionHistory.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateVersionHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.versionHistories = state.versionHistories.map((vh) =>
          vh.id === action.payload.id ? action.payload : vh
        );
      })
      .addCase(updateVersionHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      })
      .addCase(getVersionHistoryById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getVersionHistoryById.fulfilled, (state, action) => {
        state.loading = false;
        state.versionHistory = action.payload;
        state.error = null;
      })
      .addCase(getVersionHistoryById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      })
      .addCase(getAllVersionHistories.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllVersionHistories.fulfilled, (state, action) => {
        state.loading = false;
        state.versionHistories = action.payload.data?.results || action.payload.results || [];
        state.pagination = action.payload.data?.pagination || action.payload.pagination;
      })
      .addCase(getAllVersionHistories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      })
      .addCase(deleteVersionHistory.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteVersionHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.versionHistories = state.versionHistories.filter(
          (vh) => vh.id !== action.payload
        );
      })
      .addCase(deleteVersionHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      });
  },
});

export default versionHistorySlice.reducer;
